import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BadgeStaleness } from "@/components/ui/badge-staleness";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  FileText, 
  TrendingUp, 
  DollarSign, 
  Lock, 
  CheckCircle, 
  XCircle,
  Upload,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { chartColors } from "@/lib/theme/chartTheme";

interface HoldingPosition {
  id: string;
  symbol: string;
  name: string;
  assetClass: string;
  instrumentType: 'public_equity' | 'private_fund' | 'fixed_income' | 'cash' | 'real_estate' | 'hedge_fund';
  account: string;
  marketValue: number;
  costBasis: number;
  unrealizedGL: number;
  quantity?: number;
  currency: string;
  
  // For alternatives
  manager?: string;
  vehicle?: string;
  vintage?: number;
  commitment?: number;
  strategy?: string;
  
  // Historical data
  quarterlyMarks?: Array<{
    quarter: string;
    date: string;
    nav: number;
    computedNav?: number;
    overrideNav?: number;
    calledPeriod: number;
    calledCum: number;
    distributedPeriod: number;
    distributedCum: number;
    tvpi: number;
    dpi: number;
    irr: number;
    moic: number;
    status: string;
    staleDays: number;
    policy?: string;
    sourceDoc?: string;
    approvedBy?: string;
    approvedAt?: string;
    overrideReason?: string;
  }>;
  
  transactions?: Array<{
    date: string;
    type: string;
    amount: number;
    price?: number;
    quantity?: number;
    currency: string;
    sourceDoc?: string;
  }>;
  
  documents?: Array<{
    name: string;
    type: string;
    date: string;
    url?: string;
  }>;
}

interface HoldingsDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  position: HoldingPosition | null;
}

export function HoldingsDetailDrawer({ open, onClose, position }: HoldingsDetailDrawerProps) {
  if (!position) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: position.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const isAlternative = ['private_fund', 'hedge_fund', 'real_estate'].includes(position.instrumentType);
  const returnPct = ((position.unrealizedGL / position.costBasis) * 100);

  // Prepare chart data for NAV trend (for alternatives)
  const navTrendData = position.quarterlyMarks?.map(mark => ({
    quarter: mark.quarter,
    nav: mark.nav / 1000000,
    tvpi: mark.tvpi,
    dpi: mark.dpi
  })) || [];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-[900px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-2xl">
            {position.name}
            {position.symbol && <span className="text-muted-foreground ml-2">({position.symbol})</span>}
          </SheetTitle>
          <SheetDescription>
            {position.manager && `${position.manager} • `}
            {position.assetClass}
            {position.strategy && ` • ${position.strategy}`}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="marks">Quarterly Marks</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Position Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Market Value</p>
                    <p className="text-2xl font-bold">{formatCurrency(position.marketValue)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Cost Basis</p>
                    <p className="text-2xl font-semibold">{formatCurrency(position.costBasis)}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Unrealized G/L</p>
                    <p className={`text-2xl font-bold ${position.unrealizedGL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(position.unrealizedGL)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {formatPercent(returnPct)} on cost
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Account</p>
                    <p className="text-lg font-medium">{position.account}</p>
                  </div>
                </div>

                {isAlternative && (
                  <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t">
                    {position.commitment && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Commitment</p>
                        <p className="text-lg font-semibold">{formatCurrency(position.commitment)}</p>
                      </div>
                    )}
                    {position.vintage && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Vintage</p>
                        <p className="text-lg font-semibold">{position.vintage}</p>
                      </div>
                    )}
                    {position.quarterlyMarks && position.quarterlyMarks.length > 0 && (
                      <>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">TVPI</p>
                          <p className="text-lg font-semibold">
                            {position.quarterlyMarks[0].tvpi.toFixed(2)}x
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">DPI</p>
                          <p className="text-lg font-semibold">
                            {position.quarterlyMarks[0].dpi.toFixed(2)}x
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">IRR</p>
                          <p className="text-lg font-semibold">
                            {position.quarterlyMarks[0].irr.toFixed(1)}%
                          </p>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">Data Staleness</p>
                          <BadgeStaleness days={position.quarterlyMarks[0].staleDays} />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {!isAlternative && position.quantity && (
                  <div className="grid grid-cols-2 gap-6 mt-6 pt-6 border-t">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Quantity</p>
                      <p className="text-lg font-semibold">{position.quantity.toLocaleString()}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Avg Price</p>
                      <p className="text-lg font-semibold">
                        {formatCurrency(position.costBasis / position.quantity)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {navTrendData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>NAV Trend</CardTitle>
                  <CardDescription>Quarterly valuation history</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={navTrendData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="quarter" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `$${value.toFixed(0)}M`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '6px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="nav" 
                        stroke={chartColors[0]}
                        strokeWidth={2}
                        dot={{ fill: chartColors[0] }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* QUARTERLY MARKS TAB */}
          <TabsContent value="marks" className="space-y-4">
            {isAlternative && position.quarterlyMarks ? (
              <>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Valuation History</CardTitle>
                        <CardDescription>
                          Quarter-by-quarter marks, overrides, and performance
                        </CardDescription>
                      </div>
                      <Button variant="outline" size="sm">
                        <TrendingUp className="h-4 w-4" />
                        Chart View
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Quarter</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">NAV</TableHead>
                            <TableHead className="text-right">Called (P)</TableHead>
                            <TableHead className="text-right">Dist (P)</TableHead>
                            <TableHead className="text-right">TVPI</TableHead>
                            <TableHead className="text-right">DPI</TableHead>
                            <TableHead className="text-right">IRR</TableHead>
                            <TableHead>Staleness</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {position.quarterlyMarks.map((mark, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="font-medium">{mark.quarter}</TableCell>
                              <TableCell className="text-sm">{mark.date}</TableCell>
                              <TableCell className="text-right">
                                <div className="space-y-1">
                                  <div className="font-semibold">
                                    ${(mark.nav / 1000000).toFixed(2)}M
                                  </div>
                                  {mark.overrideNav && (
                                    <div className="text-xs text-muted-foreground">
                                      Base: ${(mark.computedNav || 0) / 1000000}M
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                ${(mark.calledPeriod / 1000000).toFixed(1)}M
                              </TableCell>
                              <TableCell className="text-right">
                                ${(mark.distributedPeriod / 1000000).toFixed(1)}M
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {mark.tvpi.toFixed(2)}x
                              </TableCell>
                              <TableCell className="text-right">
                                {mark.dpi.toFixed(2)}x
                              </TableCell>
                              <TableCell className="text-right">
                                {mark.irr.toFixed(1)}%
                              </TableCell>
                              <TableCell>
                                <BadgeStaleness days={mark.staleDays} />
                              </TableCell>
                              <TableCell>
                                <Badge variant={
                                  mark.status === "locked" ? "outline" :
                                  mark.status === "approved" ? "secondary" :
                                  "default"
                                }>
                                  {mark.status === "locked" && <Lock className="h-3 w-3 mr-1" />}
                                  {mark.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {position.quarterlyMarks.some(m => m.overrideReason) && (
                      <Card className="mt-4 border-blue-200 bg-blue-50/50">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                            <div className="flex-1 space-y-1">
                              <p className="text-sm font-medium">Recent Override</p>
                              <p className="text-sm text-muted-foreground">
                                {position.quarterlyMarks.find(m => m.overrideReason)?.overrideReason}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Propose Override</CardTitle>
                    <CardDescription>
                      Adjust NAV with justification and supporting documentation
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="override-nav">Override NAV</Label>
                        <Input
                          id="override-nav"
                          type="number"
                          placeholder="Enter new NAV"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="override-date">As-of Date</Label>
                        <Input id="override-date" type="date" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="override-reason">
                        Reason <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        id="override-reason"
                        placeholder="Explain why this override is needed..."
                        rows={3}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="override-doc">Supporting Document</Label>
                      <Input id="override-doc" type="file" />
                    </div>

                    <Button className="w-full">Propose Override</Button>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    Quarterly marks are not available for this asset type
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* TRANSACTIONS TAB */}
          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Transaction History</CardTitle>
                    <CardDescription>
                      {isAlternative 
                        ? "Capital calls, distributions, and cash flows"
                        : "Buys, sells, dividends, and other activity"}
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4" />
                    Record Transaction
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {position.transactions && position.transactions.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          {!isAlternative && <TableHead className="text-right">Quantity</TableHead>}
                          {!isAlternative && <TableHead className="text-right">Price</TableHead>}
                          <TableHead>Source</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {position.transactions.map((txn, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{txn.date}</TableCell>
                            <TableCell>
                              <Badge variant={
                                txn.type.includes('Distribution') || txn.type.includes('Sell') || txn.type.includes('Dividend')
                                  ? "default" 
                                  : "outline"
                              }>
                                {txn.amount > 0 ? (
                                  <ArrowUpRight className="h-3 w-3 mr-1" />
                                ) : (
                                  <ArrowDownRight className="h-3 w-3 mr-1" />
                                )}
                                {txn.type}
                              </Badge>
                            </TableCell>
                            <TableCell className={`text-right ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              <div className="flex items-center justify-end gap-1">
                                <DollarSign className="h-4 w-4" />
                                {Math.abs(txn.amount / 1000000).toFixed(2)}M
                              </div>
                            </TableCell>
                            {!isAlternative && (
                              <>
                                <TableCell className="text-right">
                                  {txn.quantity?.toLocaleString() || '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                  {txn.price ? formatCurrency(txn.price) : '-'}
                                </TableCell>
                              </>
                            )}
                            <TableCell className="text-sm text-muted-foreground">
                              {txn.sourceDoc || 'Manual entry'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No transactions recorded
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Documents</CardTitle>
                    <CardDescription>
                      Statements, reports, and supporting materials
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm">
                    <Upload className="h-4 w-4" />
                    Upload
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {position.documents && position.documents.length > 0 ? (
                  <div className="space-y-3">
                    {position.documents.map((doc, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {doc.type} • {doc.date}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm">
                          Open
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No documents available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
